import React, { useState, useEffect } from 'react';
import "../components/table.css";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  SportsSoccer,
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { sportsAPI } from '../services/api';

const SportSchema = Yup.object().shape({
  name: Yup.string().required('Sport name is required'),
  open_time: Yup.string().required('Open time is required'),
  close_time: Yup.string().required('End time is required'),
  price_per_hour: Yup.number().required('Price per hour is required').min(0, 'Price must be positive'),
  evening_price_per_hour: Yup.number().min(0, 'Price must be positive'),
});

const Sports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSport, setEditingSport] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, sport: null });
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      setLoading(true);
      const response = await sportsAPI.getAll();
      setSports(response.data.data);
    } catch (error) {
      console.error('Error fetching sports:', error);
      setAlert({ type: 'error', message: 'Failed to load sports' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    try {
      if (editingSport) {
        await sportsAPI.update(editingSport.id, values);
        setAlert({ type: 'success', message: 'Sport updated successfully' });
      } else {
        await sportsAPI.create(values);
        setAlert({ type: 'success', message: 'Sport created successfully' });
      }

      fetchSports();
      setDialogOpen(false);
      setEditingSport(null);
      resetForm();
    } catch (error) {
      console.error('Error saving sport:', error);
      setAlert({ type: 'error', message: 'Failed to save sport' });
    }
  };

  const handleDelete = async () => {
    try {
      await sportsAPI.delete(deleteDialog.sport.id);
      setAlert({ type: 'success', message: 'Sport deleted successfully' });
      fetchSports();
      setDeleteDialog({ open: false, sport: null });
    } catch (error) {
      console.error('Error deleting sport:', error);
      // Log detailed error info
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      
      // Show more detailed error message if available
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || 'Failed to delete sport. The sport may be referenced by courts or bookings.';
      setAlert({ type: 'error', message: errorMessage });
    }
  };

  const openEditDialog = (sport) => {
    setEditingSport(sport);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingSport(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingSport(null);
  };

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreateDialog}
        >
          Add Sport
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <TableContainer className="app-table-responsive">
          <Table className="app-table app-table-bordered app-table-light">
            <TableHead className="app-table-light">
              <TableRow>
                <TableCell>Sport Name</TableCell>
                <TableCell>Open Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Price (Day)</TableCell>
                <TableCell>Price (After 6 PM)</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading sports...
                  </TableCell>
                </TableRow>
              ) : sports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No sports found
                  </TableCell>
                </TableRow>
              ) : (
                sports.map((sport) => (
                  <TableRow key={sport.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SportsSoccer color="primary" />
                        {sport.name}
                      </Box>
                    </TableCell>
                    <TableCell>{sport.open_time}</TableCell>
                    <TableCell>{sport.close_time}</TableCell>
                    <TableCell>Rs {sport.price_per_hour}</TableCell>
                    <TableCell>Rs {sport.evening_price_per_hour}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(sport)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialog({ open: true, sport })}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Sport Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingSport ? 'Edit Sport' : 'Add New Sport'}
        </DialogTitle>
        <Formik
          initialValues={{
            name: editingSport?.name || '',
            open_time: editingSport?.open_time || '',
            close_time: editingSport?.close_time || '',
            price_per_hour: editingSport?.price_per_hour || '',
            evening_price_per_hour: editingSport?.evening_price_per_hour || '',
          }}
          validationSchema={SportSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, handleChange }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  margin="dense"
                  label="Sport Name"
                  name="name"
                  fullWidth
                  error={touched.name && !!errors.name}
                  helperText={touched.name && errors.name}
                  sx={{ mb: 2 }}
                />

                <Field
                  as={TextField}
                  margin="dense"
                  label="Open Time"
                  name="open_time"
                  type="time"
                  fullWidth
                  error={touched.open_time && !!errors.open_time}
                  helperText={touched.open_time && errors.open_time}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                />

                <Field
                  as={TextField}
                  margin="dense"
                  label="End Time"
                  name="close_time"
                  type="time"
                  fullWidth
                  error={touched.close_time && !!errors.close_time}
                  helperText={touched.close_time && errors.close_time}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                />

                <Field
                  as={TextField}
                  margin="dense"
                  label="Price per Hour"
                  name="price_per_hour"
                  type="number"
                  fullWidth
                  error={touched.price_per_hour && !!errors.price_per_hour}
                  helperText={touched.price_per_hour && errors.price_per_hour}
                  sx={{ mb: 2 }}
                />

                <Field
                  as={TextField}
                  margin="dense"
                  label="Price After 6 PM"
                  name="evening_price_per_hour"
                  type="number"
                  fullWidth
                  error={touched.evening_price_per_hour && !!errors.evening_price_per_hour}
                  helperText={touched.evening_price_per_hour && errors.evening_price_per_hour}
                  sx={{ mb: 2 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
                <Button type="submit" variant="contained">
                  {editingSport ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, sport: null })}
      >
        <DialogTitle>Delete Sport</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete sport "{deleteDialog.sport?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, sport: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sports;